import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DownloadIcon from '@mui/icons-material/Download';
import { CT } from './chatTheme';
import { attachmentUrl } from './chat.service';

interface Props {
  path: string;
  name: string;
  type: string;
  size?: number | null;
  mine: boolean;
  /** Lets the thread re-anchor its scroll once an image changes the height. */
  onLoaded?: () => void;
}

const prettySize = (bytes?: number | null) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * Renders one attachment. The bucket is private, so the signed URL is minted
 * on mount (and only then — no URL is stored in the message row).
 */
const MessageAttachment: React.FC<Props> = ({ path, name, type, size, mine, onLoaded }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const isImage = (type || '').startsWith('image/');

  useEffect(() => {
    let active = true;
    attachmentUrl(path)
      .then((signed) => { if (active) { setUrl(signed); setFailed(!signed); } })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [path]);

  if (failed) {
    return (
      <Typography sx={{ fontSize: 11.5, color: mine ? 'rgba(255,255,255,.85)' : CT.muted }}>
        Attachment unavailable
      </Typography>
    );
  }

  if (!url) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', width: 120, height: 60 }}>
        <CircularProgress size={16} sx={{ color: mine ? '#fff' : CT.muted }} />
      </Box>
    );
  }

  if (isImage) {
    return (
      <Box
        component="a"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ display: 'block', mt: 0.25, borderRadius: `${CT.radius.md}px`, overflow: 'hidden', lineHeight: 0 }}
      >
        <Box
          component="img"
          src={url}
          alt={name}
          loading="lazy"
          onLoad={onLoaded}
          sx={{ display: 'block', maxWidth: 220, maxHeight: 220, objectFit: 'cover' }}
        />
      </Box>
    );
  }

  return (
    <Box
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      download={name}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, px: 1, py: 0.75,
        borderRadius: `${CT.radius.md}px`, textDecoration: 'none',
        bgcolor: mine ? 'rgba(255,255,255,.16)' : CT.bone,
        color: mine ? '#fff' : CT.ink,
        border: mine ? 'none' : `1px solid ${CT.border}`,
      }}
    >
      <InsertDriveFileIcon sx={{ fontSize: 20, opacity: 0.9 }} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 600 }} noWrap>{name}</Typography>
        {size ? (
          <Typography sx={{ fontSize: 10.5, opacity: 0.75, fontFamily: CT.mono }}>{prettySize(size)}</Typography>
        ) : null}
      </Box>
      <DownloadIcon sx={{ fontSize: 16, opacity: 0.8 }} />
    </Box>
  );
};

export default MessageAttachment;
